import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";
import { useAuth } from "../../../../context/auth-context";

/** ---------------------- Tipos ---------------------- */

type PerdasConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;

  title: string;
  reportTitle: string;
  fileNamePrefix: string;
};

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

export type PerdaItemAPI = {
  id: number | string;
  movimentacao_id: number | string;
  produto_id: number | string;
  estoque_id?: number | string | null;
  quantidade: number | string;
  custo_unitario: string | number | null;
  moeda?: string | null;
  produto: {
    id: number | string;
    codigo: string;
    nome: string;
    ncm?: string | null;
    classificacao?: string | null;
    posicao: string | null;
    peso?: string | number | null;
    dimensao?: string | null;
    ocultar?: boolean | null;
  };
};

export type RetornoSolicitacaoAPI = {
  id: number | string | null;
  status: "solicitado" | "aprovado" | "negado" | null;
  quantidade: number | null;
  motivo: string | null;
  observacao: string | null;
  motivo_negativa: string | null;
  criado_em: string | null;
  analisado_em: string | null;
};

export type PerdaMovAPI = {
  movimentacao: {
    id: number | string;
    empresa_id?: number | string | null;
    estoque_id?: number | string | null;
    tipo_movimentacao_id?: number | string | null;
    codigo_movimentacao?: string | null;

    tipo?: "perda" | "servico" | "venda" | "consumo" | "outro" | string | null;
    tipo_full?: string | null;

    responsavel_id: number | string | null;
    responsavel_nome: string | null;

    os_numero?: string | null;
    proposta_venda?: string | null;
    coletor_nome?: string | null;
    embarcacao?: string | null;
    cliente_nome?: string | null;
    empresa_cliente?: string | null;

    custo_total?: string | number | null;
    motivo?: string | null;
    origem_mov_id?: number | string | null;
    coordenador_responsavel_ids?: unknown;
    criado_em: string;

    tipo_movimentacao?: {
      codigo?: string | null;
      nome?: string | null;
      natureza?: string | null;
      sinal?: number | string | null;
    };

    retorno_solicitacao?: RetornoSolicitacaoAPI | null;

    estoque?: {
      id?: number | string | null;
      codigo?: string | null;
      nome?: string | null;
      modulo_id?: number | string | null;
    };

    modulo?: {
      id?: number | string | null;
      codigo?: string | null;
      label?: string | null;
    };
  };
  itens: PerdaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  fonte?: "cache" | "banco";
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  meta: {
    filtros: {
      tipo_saida?: string | null;
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC" | "asc" | "desc";
      limit: number;
      offset: number;
    };
    count: number;
    total?: number;
    has_more?: boolean;
  };
  data: PerdaMovAPI[];
};

export type PerdaRowUI = [
  string, // Responsável
  string, // Código
  string, // Tipo
  string, // Nome do Produto
  string, // Custo Unitário
  string, // Moeda
  string, // Quantidade
  string, // Motivo
  string, // Data
  string // Hora
];

export type RetornoStatusUI = "none" | "solicitado" | "aprovado" | "negado";

export type PerdaRowMeta = {
  movimentacaoId: number;
  produtoId: number;
  itemId: number;

  responsavelId: number | null;
  responsavelNome: string | null;

  codigo: string;
  nomeProduto: string;

  tipo: string;
  tipoFull: string;

  quantidade: number;
  custoUnitario: number;
  moeda: string;
  motivo: string | null;

  retornoSolicitacaoId: number | null;
  retornoStatus: RetornoStatusUI;
  retornoQuantidade: number | null;
  retornoMotivo: string | null;
  retornoObservacao: string | null;
  retornoMotivoNegativa: string | null;
  retornoCriadoEm: string | null;
  retornoAnalisadoEm: string | null;
};

export type PerdasQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

type Row = (string | number | React.ReactNode)[];

type ItemPerdaModal = {
  originalIndex: number;
  row: Row;
  meta: PerdaRowMeta | null;
};

type ModalState = {
  open: boolean;
  responsavel: string;
  motivo: string;
  data: string;
  hora: string;
  itens: ItemPerdaModal[];
};

/** ---------------------- Constantes ---------------------- */

const URL_PERDAS =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-saidas/perdas";

const POLL_FALLBACK_MS = 15000;
const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Config / sessão ---------------------- */

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): PerdasConfig {
  const area = normalizeSlug(areaSlug) || "suprimentos";
  const estoque = normalizeSlug(estoqueSlug) || "estoque";

  const estoqueTitle = slugToTitle(estoque);

  return {
    areaSlug: area,
    estoqueSlug: estoque,
    key: `${area}-${estoque}-perdas`,
    title: `${slugToTitle(area)} - Perdas - ${estoqueTitle}`,
    reportTitle: `Relatório de Perdas - ${estoqueTitle}`,
    fileNamePrefix: `${area}-${estoque}-Perdas`,
  };
}

function findModuloAtualFromSessao(
  sessao: any,
  config: PerdasConfig
): ModuloAtual | null {
  const areaSlug = normalizeComparable(config.areaSlug);
  const estoqueSlug = normalizeComparable(config.estoqueSlug);

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoqueSlug] || estoqueSlug;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (areaSlug && depSlug && depSlug !== areaSlug) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoqueSlug ||
            modLabel === estoqueSlug ||
            modCodigo.includes(estoqueSlug) ||
            estoqueSlug.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
      return {
        data: iso,
        hora: "",
      };
    }

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return { data, hora };
  } catch {
    return { data: iso, hora: "" };
  }
}

function formatMoney(valor: number, moeda?: string | null): string {
  const currency = String(moeda || "BRL").toUpperCase();

  const symbol =
    currency === "USD"
      ? "$"
      : currency === "EUR"
      ? "€"
      : currency === "BRL"
      ? "R$"
      : currency;

  return `${symbol} ${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function parseQuantidade(value: unknown): number {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function parseCurrency(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const raw = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .trim();

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma > lastDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = raw.replace(/,/g, "");
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function normalizeRetornoStatus(
  status: RetornoSolicitacaoAPI["status"] | undefined | null
): RetornoStatusUI {
  if (status === "solicitado") return "solicitado";
  if (status === "aprovado") return "aprovado";
  if (status === "negado") return "negado";
  return "none";
}

function toNumberId(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) ? n : 0;
}

function isPerda(mov: PerdaMovAPI["movimentacao"]): boolean {
  const tipo = String(mov.tipo ?? "").toLowerCase();
  const tipoFull = String(mov.tipo_full ?? "").toLowerCase();
  const codigoMov = String(mov.codigo_movimentacao ?? "").toUpperCase();
  const tipoMovCodigo = String(
    mov.tipo_movimentacao?.codigo ?? ""
  ).toUpperCase();

  if (codigoMov === "BAIXA-PERDA") return true;
  if (tipoMovCodigo === "BAIXA-PERDA") return true;
  if (tipo === "perda") return true;
  if (tipoFull === "perda") return true;
  if (tipoFull === "saida-perda") return true;
  if (tipoFull === "baixa-perda") return true;
  if (tipoFull.includes("perda")) return true;

  return false;
}

function flattenToRows(movs: PerdaMovAPI[]): {
  rows: PerdaRowUI[];
  rowMeta: PerdaRowMeta[];
} {
  const rows: PerdaRowUI[] = [];
  const rowMeta: PerdaRowMeta[] = [];

  for (const mov of movs || []) {
    if (!isPerda(mov.movimentacao)) continue;

    const { data, hora } = toBRT(mov.movimentacao.criado_em);

    const responsavel = mov.movimentacao.responsavel_nome?.trim() || "-";
    const motivo = mov.movimentacao.motivo?.trim() || "-";
    const tipo = mov.movimentacao.tipo?.trim() || "perda";
    const tipoFull = mov.movimentacao.tipo_full?.trim() || "perda";

    const retorno = mov.movimentacao.retorno_solicitacao || null;
    const retornoStatus = normalizeRetornoStatus(retorno?.status);

    for (const item of mov.itens || []) {
      const quantidade = parseQuantidade(item.quantidade);
      const custo = parseQuantidade(item.custo_unitario);
      const moeda = String(item.moeda || "BRL").toUpperCase();

      const codigo = String(item.produto?.codigo ?? "").toUpperCase();
      const nomeProduto = String(item.produto?.nome ?? "").toUpperCase();

      rows.push([
        responsavel,
        codigo,
        tipo,
        nomeProduto,
        formatMoney(Number.isFinite(custo) ? custo : 0, moeda),
        moeda,
        String(Number.isFinite(quantidade) ? quantidade : 0),
        motivo,
        data,
        hora,
      ]);

      rowMeta.push({
        movimentacaoId: toNumberId(mov.movimentacao.id),
        produtoId: toNumberId(item.produto.id),
        itemId: toNumberId(item.id),

        responsavelId:
          mov.movimentacao.responsavel_id === null ||
          mov.movimentacao.responsavel_id === undefined
            ? null
            : toNumberId(mov.movimentacao.responsavel_id),
        responsavelNome: mov.movimentacao.responsavel_nome,

        codigo,
        nomeProduto,

        tipo,
        tipoFull,

        quantidade: Number.isFinite(quantidade) ? quantidade : 0,
        custoUnitario: Number.isFinite(custo) ? custo : 0,
        moeda,
        motivo: mov.movimentacao.motivo ?? null,

        retornoSolicitacaoId:
          retorno?.id === null || retorno?.id === undefined
            ? null
            : toNumberId(retorno.id),
        retornoStatus,
        retornoQuantidade: retorno?.quantidade ?? null,
        retornoMotivo: retorno?.motivo ?? null,
        retornoObservacao: retorno?.observacao ?? null,
        retornoMotivoNegativa: retorno?.motivo_negativa ?? null,
        retornoCriadoEm: retorno?.criado_em ?? null,
        retornoAnalisadoEm: retorno?.analisado_em ?? null,
      });
    }
  }

  return {
    rows,
    rowMeta,
  };
}

/** ---------------------- Fetchers ---------------------- */

async function fetchPerdasOnce(
  moduloAtual: ModuloAtual | null,
  query?: PerdasQuery
): Promise<{
  rows: PerdaRowUI[];
  rowMeta: PerdaRowMeta[];
  meta: RespAPI["meta"] | null;
  resp: RespAPI | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota");
  }

  const payload = {
    modulo_id: moduloAtual.id,
    order: query?.order ?? "desc",
    limit: query?.limit ?? 10000,
    offset: query?.offset ?? 0,
    ...(query?.date_from ? { date_from: query.date_from } : {}),
    ...(query?.date_to ? { date_to: query.date_to } : {}),
  };

  const res = await fetch(URL_PERDAS, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const parsedJson = (await res.json().catch(() => null)) as RespAPI | null;

  if (!res.ok || parsedJson?.ok === false) {
    const erro =
      (parsedJson as any)?.erro ||
      (Array.isArray((parsedJson as any)?.erros) &&
        (parsedJson as any).erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(erro);
  }

  const dataRaw: PerdaMovAPI[] =
    parsedJson && Array.isArray(parsedJson.data) ? parsedJson.data : [];

  const flat = flattenToRows(dataRaw);

  const meta: RespAPI["meta"] | null =
    parsedJson && parsedJson.meta ? parsedJson.meta : null;

  return {
    rows: flat.rows,
    rowMeta: flat.rowMeta,
    meta,
    resp: parsedJson,
  };
}

/** ---------------------- Hook genérico ---------------------- */

function usePerdasGenerico(
  moduloAtual: ModuloAtual | null,
  authLoading: boolean,
  query?: PerdasQuery
) {
  const [rows, setRows] = useState<PerdaRowUI[]>([]);
  const [rowMeta, setRowMeta] = useState<PerdaRowMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [resp, setResp] = useState<RespAPI | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchPerdasOnce(moduloAtual, query);

      setRows(result.rows);
      setRowMeta(result.rowMeta);
      setMeta(result.meta);
      setResp(result.resp);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar perdas.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    setLoading(true);
    setErro(null);
    setRows([]);
    setRowMeta([]);
    setMeta(null);
    setResp(null);

    if (authLoading) {
      return () => {
        stop.current = true;
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");
      return () => {
        stop.current = true;
      };
    }

    (async () => {
      try {
        await carregar();
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(jitter(POLL_FALLBACK_MS));

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("usePerdasGenerico loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, authLoading, JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    rowMeta,
    loading,
    refresh,
    meta,
    resp,
    erro,
  };
}

/** ---------------------- Modal styles ---------------------- */

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1120,
  background: "#ffffff",
  borderRadius: 0,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  border: "1px solid #0c2b53",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #0c2b53",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#111827",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 12px 0",
  fontSize: 14,
  fontWeight: 600,
  color: "#0c2b53",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 12,
  marginBottom: 20,
};

const fieldBoxStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  background: "#ffffff",
};

const fieldLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#0c2b53",
  marginBottom: 4,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#111827",
  fontWeight: 500,
  wordBreak: "break-word",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #0c2b53",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  background: "#ffffff",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#ffffff",
  color: "#111827",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonPrimaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#0c2b53",
  color: "#ffffff",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

/** ---------------------- Página ---------------------- */

const SaidasPerdasGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const { sessao, loading: authLoading } = useAuth();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const moduloAtual = useMemo(
    () => findModuloAtualFromSessao(sessao, config),
    [sessao, config]
  );

  const headers = useMemo(
    () => [
      "Responsável",
      "Código",
      "Tipo",
      "Nome do Produto",
      "Custo Unitário",
      "Moeda",
      "Quantidade",
      "Motivo",
      "Data",
      "Hora",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "140px",
      "70px",
      "80px",
      "270px",
      "100px",
      "70px",
      "60px",
      "150px",
      "80px",
      "60px",
    ],
    []
  );

  const { rows, rowMeta, loading, erro, resp } = usePerdasGenerico(
    moduloAtual,
    authLoading
  );

  const [modal, setModal] = useState<ModalState>({
    open: false,
    responsavel: "",
    motivo: "",
    data: "",
    hora: "",
    itens: [],
  });

  const pageLoading = authLoading || loading;

  const title = useMemo(() => {
    const estoqueNome = resp?.estoque?.nome;
    const moduloLabel = resp?.modulo?.label || moduloAtual?.label;

    if (estoqueNome) {
      return `${slugToTitle(config.areaSlug)} - Perdas - ${estoqueNome}`;
    }

    if (moduloLabel) {
      return `${slugToTitle(config.areaSlug)} - Perdas - ${moduloLabel}`;
    }

    return config.title;
  }, [config.areaSlug, config.title, resp, moduloAtual]);

  const reportTitle = useMemo(() => {
    const estoqueNome = resp?.estoque?.nome;

    if (estoqueNome) {
      return `Relatório de Perdas - ${estoqueNome}`;
    }

    return config.reportTitle;
  }, [config.reportTitle, resp]);

  const enabledColumnIndexes = useMemo(
    () => headers.map((_header, index) => index),
    [headers]
  );

  const totalQuantidade = useMemo(() => {
    return rows.reduce((acc, row) => acc + parseQuantidade(row[6]), 0);
  }, [rows]);

  const totaisPorMoeda = useMemo(() => {
    const acc: Record<string, number> = {};

    rows.forEach((row) => {
      const custo = parseCurrency(row[4]);
      const moeda = String(row[5] || "BRL").toUpperCase();
      const quantidade = parseQuantidade(row[6]);

      const incremento =
        Number.isFinite(custo) && Number.isFinite(quantidade)
          ? custo * quantidade
          : 0;

      if (!acc[moeda]) acc[moeda] = 0;

      acc[moeda] += incremento;
    });

    return acc;
  }, [rows]);

  const footerLines = useMemo(() => {
    const lines: string[] = [];

    lines.push(`Total de Itens em Perda: ${totalQuantidade}`);

    Object.entries(totaisPorMoeda)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([moeda, valor]) => {
        lines.push(
          `Valor Total das Perdas (${moeda}): ${formatMoney(valor, moeda)}`
        );
      });

    return lines;
  }, [totalQuantidade, totaisPorMoeda]);

  const gerarRelatorioPerdas = useCallback(() => {
    generatePdfReport({
      reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações da Saída de Materiais como Perdas",
      headers,
      rows,
      custoColIndex: 4,
      enabledColumns: enabledColumnIndexes,
      footerLines,
    });
  }, [
    reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    enabledColumnIndexes,
    footerLines,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const base = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/saidas/perdas`
      );

      const isPerdasContext =
        currentPath.includes(base) ||
        eventPath.includes(base) ||
        currentPath.includes("perdas") ||
        eventPath.includes("perdas") ||
        moduloLabel.includes("perdas") ||
        moduloCodigo.includes("perdas") ||
        moduloSlug.includes("perdas");

      if (!isPerdasContext) return;

      gerarRelatorioPerdas();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioPerdas]);

  const openModalPorLinha = (rowBase: Row, indexBase: number) => {
    const responsavel = String(rowBase[0] ?? "-");
    const motivo = String(rowBase[7] ?? "-");
    const data = String(rowBase[8] ?? "-");
    const hora = String(rowBase[9] ?? "-");

    setModal({
      open: true,
      responsavel,
      motivo,
      data,
      hora,
      itens: [
        {
          originalIndex: indexBase,
          row: rowBase,
          meta: rowMeta[indexBase] ?? null,
        },
      ],
    });
  };

  const closeModal = () => {
    setModal({
      open: false,
      responsavel: "",
      motivo: "",
      data: "",
      hora: "",
      itens: [],
    });
  };

  const modalTotals = useMemo(() => {
    let totalItens = 0;
    let totalQuantidadeModal = 0;
    const totalPorMoeda: Record<string, number> = {};

    for (const item of modal.itens) {
      const quantidade = parseQuantidade(item.row[6]);
      const custo = parseCurrency(item.row[4]);
      const moeda = String(item.row[5] || "BRL").toUpperCase();

      totalItens += 1;
      totalQuantidadeModal += quantidade;

      if (!totalPorMoeda[moeda]) totalPorMoeda[moeda] = 0;

      totalPorMoeda[moeda] += quantidade * custo;
    }

    return {
      totalItens,
      totalQuantidade: totalQuantidadeModal,
      totalPorMoeda,
    };
  }, [modal.itens]);

  const gerarRelatorioDoModal = () => {
    if (!modal.itens.length) return;

    const modalRows = modal.itens.map((item) => item.row);

    const modalFooterLines = [
      `Itens na perda: ${modalTotals.totalItens}`,
      `Quantidade total: ${modalTotals.totalQuantidade}`,
      ...Object.entries(modalTotals.totalPorMoeda)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
          ([moeda, valor]) =>
            `Custo total estimado (${moeda}): ${formatMoney(valor, moeda)}`
        ),
    ];

    generatePdfReport({
      reportTitle: "Relatório de Perda",
      fileNamePrefix: `Perda-${modal.responsavel}-${modal.data}`.replace(
        /[^\w\-]+/g,
        "_"
      ),
      subtitle: `Perda de materiais - Responsável: ${modal.responsavel} | Motivo: ${modal.motivo} | Data: ${modal.data} ${modal.hora}`,
      headers,
      rows: modalRows,
      custoColIndex: 4,
      enabledColumns: enabledColumnIndexes,
      footerLines: modalFooterLines,
    });
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={title} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {pageLoading ? (
        <div style={{ padding: 16 }}>Carregando perdas…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          onDoubleClick={(viewIndex, rowData, originalIndex) => {
            const idx = originalIndex ?? viewIndex;
            const row = (rows[idx] ?? rowData) as Row;

            if (!row) return;

            openModalPorLinha(row, idx);
          }}
        />
      )}

      {modal.open && (
        <div style={overlayStyle} onClick={closeModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>Detalhes da perda</h3>

                <div style={{ marginTop: 6, color: "#374151", fontSize: 14 }}>
                  <strong>{modal.responsavel}</strong> — {modal.motivo}
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              <h4 style={sectionTitleStyle}>Resumo da perda</h4>

              <div style={gridStyle}>
                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Responsável</span>
                  <div style={fieldValueStyle}>{modal.responsavel || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Motivo</span>
                  <div style={fieldValueStyle}>{modal.motivo || "-"}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Data / Hora</span>
                  <div style={fieldValueStyle}>
                    {modal.data || "-"} {modal.hora || ""}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Itens</span>
                  <div style={fieldValueStyle}>{modalTotals.totalItens}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Quantidade total</span>
                  <div style={fieldValueStyle}>
                    {modalTotals.totalQuantidade}
                  </div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Custo total estimado</span>
                  <div style={fieldValueStyle}>
                    {Object.keys(modalTotals.totalPorMoeda).length
                      ? Object.entries(modalTotals.totalPorMoeda)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([moeda, valor]) => (
                            <div key={moeda}>{formatMoney(valor, moeda)}</div>
                          ))
                      : "R$ 0,00"}
                  </div>
                </div>
              </div>

              <h4 style={sectionTitleStyle}>Materiais</h4>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr>
                      {headers.map((header) => (
                        <th
                          key={header}
                          style={{
                            border: "1px solid #0c2b53",
                            padding: 8,
                            background: "#f8fafc",
                            color: "#0c2b53",
                            textAlign: "left",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {modal.itens.map((item) => (
                      <tr key={item.originalIndex}>
                        {headers.map((_header, index) => (
                          <td
                            key={index}
                            style={{
                              border: "1px solid #0c2b53",
                              padding: 8,
                              color: "#111827",
                            }}
                          >
                            {item.row[index] ?? "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={modalFooterStyle}>
              <button
                type="button"
                onClick={closeModal}
                style={buttonSecondaryStyle}
              >
                Fechar
              </button>

              <button
                type="button"
                onClick={gerarRelatorioDoModal}
                style={buttonPrimaryStyle}
              >
                Gerar Relatório da Perda
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaidasPerdasGenerico;
